import { afterNextRender, DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { JwtHelperService } from '@auth0/angular-jwt';
import { DjangoService } from '@core/services/django.service';
import { LOCALSTORAGE } from '@core/services/local-storage.service';
import { TabIdleService } from '@core/services/tab-idle.service';
import { httpRetry } from '@core/utils/httpRetry';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
    catchError,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    switchMap,
    tap,
} from 'rxjs/operators';

export interface ITokenStorageService {
    storeTokens(jwt: string, refreshToken: string): void;
    getToken(): Observable<string>;
    removeTokens(): void;
}

@Injectable({
    providedIn: 'root',
})
export class TokenStorageService implements ITokenStorageService {
    private token: string;
    private readonly JWT_TOKEN = 'JWT_TOKEN';
    private readonly REFRESH_TOKEN = 'REFRESH_TOKEN';
    private djangoService = inject(DjangoService);
    private localStorage: Storage = inject(LOCALSTORAGE);
    private jwtHelper: JwtHelperService = new JwtHelperService();
    private tabIdleService = inject(TabIdleService);
    private destroyRef = inject(DestroyRef);

    // The number of seconds before the token expires to refresh it
    private readonly NUMBER_OF_SECONDS_TO_EXPIRE = 30;
    // number of seconds before triggering the refresh token.
    // should be less than NUMBER_OF_SECONDS_TO_EXPIRE
    private readonly NUMBER_OF_SECONDS_TO_REFRESH_BEFORE_EXPIRE = 20;

    private refreshSubject = new BehaviorSubject<string>(null);

    private isTabIdle = this.tabIdleService.getIdle(60 * 1000).pipe(
        distinctUntilChanged(),
        filter(idle => !idle)
    );

    constructor() {
        afterNextRender(() => {
            this.tokenObservable
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe();
        });
    }

    private tokenObservable = this.refreshSubject
        .pipe(
            switchMap(() => this.isTabIdle),
            map(() => {
                if (!this.getJwtToken()) {
                    console.log('Not Authenticated!');
                    return [null, null];
                }
                return [this.getJwtToken(), this.getRefreshToken()];
            })
        )
        .pipe(
            switchMap(([token, refreshToken]) => {
                /**
                 * Check if refresh token is expired.
                 * If it is expired, logout the user and remove tokens.
                 * */
                if (!token) {
                    return of([token, refreshToken]);
                }
                if (
                    this.jwtHelper.isTokenExpired(
                        refreshToken,
                        this.NUMBER_OF_SECONDS_TO_EXPIRE
                    )
                ) {
                    console.error('Refresh Token expired');
                    return this.djangoService.logout().pipe(
                        tap(() => this.removeTokens()),
                        map(() => [null, null])
                    );
                }
                return of([token, refreshToken]);
            }),
            switchMap(([token, refreshToken]) => {
                /**
                 * Check if access token is expired.
                 * If it is expired, refresh the token.
                 * If there is an error refreshing the token, logout the user and remove tokens.
                 * */
                if (!token) {
                    return of([token, refreshToken]);
                }
                if (
                    this.jwtHelper.isTokenExpired(
                        token,
                        this.NUMBER_OF_SECONDS_TO_EXPIRE
                    )
                ) {
                    console.error('Access Token expired');
                    return this.refreshToken().pipe(
                        map(data => [data.access, data.refresh]),
                        tap(() => console.log('Token refreshed')),
                        catchError(() => {
                            return this.djangoService.logout().pipe(
                                tap(() => this.removeTokens()),
                                map(() => [null, null])
                            );
                        })
                    );
                }
                return of([token, refreshToken]);
            }),
            distinctUntilChanged(
                (previous, current) =>
                    previous[0] === current[0] && previous[1] === current[1]
            ),
            tap(([token, refreshToken]) => {
                /**
                 * Set timeout to refresh the token before it expires.
                 * */
                if (!token) {
                    return of([token, refreshToken]);
                }
                const expiresIn = this.getTokenExpirationFromNow(
                    token,
                    refreshToken
                );
                setTimeout(
                    () => {
                        this.refreshSubject.next(null);
                    },
                    expiresIn -
                        this.NUMBER_OF_SECONDS_TO_REFRESH_BEFORE_EXPIRE * 1000
                );
            }),
            map(([token, refreshToken]) => token),
            shareReplay(1),
            filter(
                // filter again to make sure once the access token is expired, we're waiting for the new one
                token =>
                    token === null ||
                    !this.jwtHelper.isTokenExpired(
                        token,
                        this.NUMBER_OF_SECONDS_TO_EXPIRE
                    )
            )
        );

    private getJwtToken() {
        if (!this.token) {
            this.token = this.localStorage.getItem(this.JWT_TOKEN);
        }
        return this.token;
    }

    private getRefreshToken() {
        return this.localStorage.getItem(this.REFRESH_TOKEN);
    }

    private getTokenExpirationFromNow(
        accessToken: string,
        refreshToken: string
    ) {
        const now = Date.now();
        const tokenExpiresIn =
            this.jwtHelper.getTokenExpirationDate(accessToken).getTime() - now;
        const refreshTokenExpiresIn =
            this.jwtHelper.getTokenExpirationDate(refreshToken).getTime() - now;
        return Math.min(tokenExpiresIn, refreshTokenExpiresIn);
    }

    storeTokens(jwt, refreshToken) {
        this.token = jwt;
        this.localStorage.setItem(this.JWT_TOKEN, jwt);
        this.localStorage.setItem(this.REFRESH_TOKEN, refreshToken);
        this.refreshSubject.next(null);
    }

    getToken(): Observable<string> {
        return this.tokenObservable;
    }

    removeTokens() {
        this.token = null;
        this.localStorage.removeItem(this.JWT_TOKEN);
        this.localStorage.removeItem(this.REFRESH_TOKEN);
        this.refreshSubject.next(null);
    }

    private refreshToken() {
        const data = {
            refresh: this.getRefreshToken(),
        };
        return this.djangoService.refresh(data).pipe(
            httpRetry(3, 200),
            tap((data: any) => {
                this.storeTokens(data.access, data.refresh);
            })
        );
    }
}
